import moment, { Moment } from 'moment';
import { User } from '../../../types/User';
import { useEffect, useState } from 'react';
import { getAmbassadors, updateUser } from '../../../services/users';
import Modal from '@mui/material/Modal';
import { DatePicker } from '@mui/x-date-pickers';
import { Calendar, DollarSign, Edit3, Eye } from 'react-feather';
import { TextField, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

interface Advocate extends User {
  referralCount: number;
  subscriberCount: number;
  referralEmails: string[];
}

interface AdvocateDashboardProps {
  hideSynaptiq: boolean;
}

const AdvocateDashboard: React.FC<AdvocateDashboardProps> = ({
  hideSynaptiq,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);

  const [ambassadors, setAmbassadors] = useState<Advocate[]>([]);
  const [showAdvocateReferralsModal, setShowAdvocateReferralsModal] =
    useState(false);
  const [advocateReferrals, setAdvocateReferrals] = useState<string[]>([]);
  const [pendingModifyUserId, setPendingModifyUserId] = useState<string | null>(
    null
  );
  const [
    showAmbassadorEndDateModificationModal,
    setShowAmbassadorEndDateModificationModal,
  ] = useState(false);
  const [
    showAmbassadorPaymentModificationModal,
    setShowAmbassadorPaymentModificationModal,
  ] = useState(false);
  const [pendingAmbassadorPayment, setPendingAmbassadorPayment] = useState<
    string | null
  >(null);
  const [pendingAmbassadorEndDate, setPendingAmbassadorEndDate] =
    useState<Moment | null>(null);

  useEffect(() => {
    fetchAmbassadors();
  }, []);

  useEffect(() => {
    fetchAmbassadors();
  }, [hideSynaptiq]);

  const fetchAmbassadors = async () => {
    try {
      const fetchedAmbassadors = await getAmbassadors();
      setAmbassadors(fetchedAmbassadors || []);
    } catch (error) {
      console.error('Error fetching ambassadors:', error);
    }
  };

  const loadAdvocateReferrals = async (userId: string) => {
    const advocate = ambassadors.find((ambassador) => ambassador.id === userId);
    if (advocate) {
      setAdvocateReferrals(advocate.referralEmails);
      setShowAdvocateReferralsModal(true);
    }
  };

  const modifyAmbassadorPaymentModification = async () => {
    if (!pendingAmbassadorPayment || !pendingModifyUserId) return;
    await updateUser(pendingModifyUserId, {
      ambassadorPayment: pendingAmbassadorPayment,
    });
    setShowAmbassadorPaymentModificationModal(false);
    setPendingAmbassadorPayment(null);
    setPendingModifyUserId(null);
    fetchAmbassadors();
  };

  const computeReferralDaysRemaining = (user: Advocate) => {
    if (user.ambassadorEndDate) {
      const endDate = user.ambassadorEndDate;
      const daysRemaining = moment(endDate).diff(moment(), 'days');
      return daysRemaining > 0 ? daysRemaining : 0;
    } else {
      const createdAt = user.ambassadorStartDate
        ? moment(user.ambassadorStartDate)
        : moment(user.createdAt);
      const oneMonthFromCreation = createdAt.add(36, 'days');
      const daysRemaining = oneMonthFromCreation.diff(moment(), 'days');
      return daysRemaining > 0 ? daysRemaining : 0;
    }
  };

  const toggleAmbassadorPaid = async (userId: string) => {
    const user = ambassadors.find((ambassador) => ambassador.id === userId);
    if (!user) return;
    await updateUser(userId, {
      ambassadorPaid: !user.ambassadorPaid,
    });
    fetchAmbassadors();
  };

  const modifyAmbassadorDayModification = async () => {
    if (!pendingAmbassadorEndDate || !pendingModifyUserId) return;
    const user = ambassadors.find(
      (ambassador) => ambassador.id === pendingModifyUserId
    );
    if (!user) return;
    await updateUser(pendingModifyUserId, {
      ambassadorEndDate: pendingAmbassadorEndDate.toDate(),
    });
    setShowAmbassadorEndDateModificationModal(false);
    setPendingAmbassadorEndDate(null);
    setPendingModifyUserId(null);
    fetchAmbassadors();
  };
  return (
    <>
      <table className="users-list">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Referral Count</th>
            <th>Subscriber Count</th>
            <th>Payment</th>
            <th>Paid</th>
            <th>Days Remaining</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {ambassadors
            .sort((a, b) => {
              if (a.ambassadorPaid === b.ambassadorPaid) return 0;
              return a.ambassadorPaid ? 1 : -1;
            })
            .map((user: Advocate) => (
              <tr key={user.id} className={user.ambassadorPaid ? 'paid' : ''}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>
                  {user.ambassadorStartDate
                    ? moment(user.ambassadorStartDate).format('MM/DD/YYYY')
                    : ''}
                </td>
                <td>
                  {user.ambassadorEndDate
                    ? moment(user.ambassadorEndDate).format('MM/DD/YYYY')
                    : ''}
                </td>
                <td>{user.referralCount}</td>
                <td>{user.subscriberCount}</td>
                <td>{user.ambassadorPayment}</td>
                <td>{user.ambassadorPaid ? 'Yes' : 'No'}</td>
                <td>{computeReferralDaysRemaining(user)}</td>
                <td style={{ display: 'flex', gap: '0.5rem' }}>
                  <Tooltip title="View Referred Students">
                    <button
                      className="button button--icon-only"
                      onClick={() => loadAdvocateReferrals(user.id)}
                    >
                      <Eye />
                    </button>
                  </Tooltip>
                  <Tooltip title="Toggle Paid Status">
                    <button
                      className="button button--icon-only"
                      onClick={() => toggleAmbassadorPaid(user.id)}
                    >
                      <DollarSign />
                    </button>
                  </Tooltip>
                  {currentUser?.email === 'jacob@synaptiq.co' && (
                    <Tooltip title="Modify Payment Notes">
                      <button
                        className="button button--icon-only"
                        onClick={() => {
                          setShowAmbassadorPaymentModificationModal(true);
                          setPendingModifyUserId(user.id);
                          setPendingAmbassadorPayment(user.ambassadorPayment);
                        }}
                      >
                        <Edit3 />
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip title="Modify Advocate End Date">
                    <button
                      className="button button--icon-only"
                      onClick={() => {
                        setShowAmbassadorEndDateModificationModal(true);
                        setPendingModifyUserId(user.id);
                        setPendingAmbassadorEndDate(
                          user.ambassadorEndDate
                            ? moment(user.ambassadorEndDate)
                            : null
                        );
                      }}
                    >
                      <Calendar />
                    </button>
                  </Tooltip>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      <Modal
        open={showAmbassadorEndDateModificationModal}
        onClose={() => {
          setShowAmbassadorEndDateModificationModal(false);
          setPendingAmbassadorEndDate(null);
          setPendingModifyUserId(null);
        }}
      >
        <div className="modal modal--ambassador-end-date-modification">
          <div className="modal_header">
            <h2>Modify Ambassador End Date</h2>
          </div>
          <div
            className="modal_content"
            style={{ backgroundColor: 'rgba(0,0,0,.5)' }}
          >
            <DatePicker
              value={pendingAmbassadorEndDate}
              onChange={(e) => setPendingAmbassadorEndDate(e)}
            />
          </div>
          <div className="modal_actions">
            <button
              className="button"
              onClick={modifyAmbassadorDayModification}
            >
              Submit
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        open={showAmbassadorPaymentModificationModal}
        onClose={() => {
          setShowAmbassadorPaymentModificationModal(false);
          setPendingAmbassadorPayment(null);
          setPendingModifyUserId(null);
        }}
      >
        <div className="modal modal--ambassador-payment-modification">
          <div className="modal_header">
            <h2>Modify Ambassador Payment</h2>
          </div>
          <div className="modal_content">
            <TextField
              label="Payment"
              value={pendingAmbassadorPayment || ''}
              onChange={(e) => setPendingAmbassadorPayment(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div className="modal_actions">
            <button
              className="button"
              onClick={modifyAmbassadorPaymentModification}
            >
              Submit
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        open={showAdvocateReferralsModal}
        onClose={() => setShowAdvocateReferralsModal(false)}
      >
        <div className="modal modal--advocate-referrals">
          <div className="modal_header">
            <h2>Advocate Referrals</h2>
          </div>
          <div className="modal_content">
            {advocateReferrals.map((referral) => (
              <div key={'advocate-referral-' + referral}>
                <p>{referral}</p>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AdvocateDashboard;
